import { z } from 'zod/v4';

/**
 * One option of a radio/select form element. Mirrors `Option` from
 * `common/types/form-element.type.ts`.
 */
export const formElementOptionSchema = z.object({
    value: z.string(),
    label: z.string(),
    selected: z.boolean(),
});

/**
 * Chat-bot variable payload attached to a form element (one per messenger).
 * Mirrors `VariableData` from `common/types/form-element.type.ts` — the raw
 * bot-variable API object the editor stores after the user maps a field.
 */
export const variableDataSchema = z.object({
    created_at: z.string(),
    bot_id: z.string(),
    _id: z.string(),
    name: z.string(),
    updated_at: z.string(),
    value: z.number(),
    value_type: z.number(),
});

/**
 * Form element kind — the union of `EFormInputType` and `EFormControlType`
 * values from the host app.
 */
export const formElementTypeSchema = z.enum([
    // EFormInputType
    'input',
    'name',
    'email',
    'phone',
    'address',
    'date',
    'url',
    'question',
    'hidden',
    'promocode',
    'password',
    'file',
    'number',
    // EFormControlType
    'checkbox',
    'radio',
    'select',
    'textarea',
]);

/**
 * HTML input type rendered for the element, mirroring `EHtmlInputType`.
 */
export const htmlInputTypeSchema = z.enum([
    'email',
    'tel',
    'url',
    'date',
    'text',
    'checkbox',
    'radio',
    'select',
    'hidden',
    'textarea',
    'file',
    'password',
    'number',
    'multiselect',
]);

/**
 * Autocomplete attribute value, mirroring `EHtmlAutocompleteAttr`.
 */
export const htmlAutocompleteSchema = z.enum(['email', 'name', 'street-address', 'tel', 'url', 'on', 'off']);

/**
 * One element of the form widget's `elements` array — the host app's
 * `FormElement` union (input/date/checkbox/radio/select/file/textarea/number)
 * flattened into a single strict-field object: the per-type extras
 * (`placeholder`, `htmlInputType`, `autocompleteType`, `options`) are
 * optional, everything every variant shares is required. A discriminated
 * union over the 17 `type` values would buy little (9 of them share the
 * plain-input shape) at a large brittleness cost against legacy data.
 */
export const formElementSchema = z.object({
    type: formElementTypeSchema,
    label: z.string().nullable(),
    variable: z.string().nullable(),
    crmContactVariable: z.union([z.number(), z.string()]).nullish(),
    crmDealVariable: z.union([z.number(), z.string()]).nullish(),
    telegramVariable: variableDataSchema.nullish(),
    instagramVariable: variableDataSchema.nullish(),
    viberVariable: variableDataSchema.nullish(),
    messengerVariable: variableDataSchema.nullish(),
    whatsappVariable: variableDataSchema.nullish(),
    tiktokVariable: variableDataSchema.nullish(),
    required: z.boolean().nullish(),
    protected: z.boolean().nullish(),
    isDynamicPlaceholderEnabled: z.boolean().nullish(),
    crmCommentPlace: z.enum(['crmDealComment', 'crmContactComment', 'crmAllComment']).nullish(),
    value: z.unknown().optional(),

    // Per-type extras
    placeholder: z.string().nullish(),
    htmlInputType: htmlInputTypeSchema.nullish(),
    autocompleteType: htmlAutocompleteSchema.nullish(),
    options: z.array(formElementOptionSchema).optional(),
});

export type FormElementOption = z.infer<typeof formElementOptionSchema>;
export type FormElementVariableData = z.infer<typeof variableDataSchema>;
export type FormWidgetElement = z.infer<typeof formElementSchema>;
